/**
 * Central de Catálogos — criado 02/09/2026 (Sabe).
 * Cadastro do material que se repete por ciclo (catálogo, tabela de preços, encarte).
 * Restrito a administradores; as atendentes só escolhem o catálogo dentro da conversa.
 * Genérico: não conhece cliente nem canal (oficial ou não).
 */
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import UploadFile from "@mui/icons-material/UploadFile";
import { toast } from "react-toastify";

import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const emMB = (bytes) => (!bytes ? "—" : `${(bytes / 1048576).toFixed(1)} MB`);

const dataBR = (iso) => {
  if (!iso) return "sem validade";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "sem validade";
  return d.toLocaleDateString("pt-BR", { timeZone: "UTC" });
};

const vencido = (iso) => {
  if (!iso) return false;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return new Date(`${String(iso).slice(0, 10)}T00:00:00`) < hoje;
};

const Catalogos = () => {
  const { user } = useContext(AuthContext);
  const [catalogos, setCatalogos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [confirmar, setConfirmar] = useState(null);
  const [editando, setEditando] = useState(null);

  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [validoAte, setValidoAte] = useState("");
  const [arquivo, setArquivo] = useState(null);
  const inputArquivo = useRef(null);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get("/catalogos", { params: { todos: true } });
      setCatalogos(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const limparFormulario = () => {
    setEditando(null);
    setNome("");
    setDescricao("");
    setValidoAte("");
    setArquivo(null);
    if (inputArquivo.current) inputArquivo.current.value = "";
  };

  const cadastrar = async () => {
    if (!nome.trim()) return toast.warn("Dê um nome ao catálogo");
    if (!arquivo) return toast.warn("Escolha o arquivo");

    const dados = new FormData();
    dados.append("nome", nome.trim());
    dados.append("descricao", descricao.trim());
    if (validoAte) dados.append("validoAte", validoAte);
    dados.append("arquivo", arquivo);

    setEnviando(true);
    try {
      const { data } = await api.post("/catalogos", dados, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.reducaoPercentual > 0) {
        toast.success(
          `Catálogo cadastrado. O arquivo ficou ${data.reducaoPercentual}% menor ` +
            `(${emMB(data.tamanhoOriginal)} → ${emMB(data.tamanho)}).`
        );
      } else {
        toast.success("Catálogo cadastrado.");
      }
      setModalAberto(false);
      limparFormulario();
      carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setEnviando(false);
    }
  };

  const abrirEdicao = (catalogo) => {
    setEditando(catalogo);
    setNome(catalogo.nome || "");
    setDescricao(catalogo.descricao || "");
    setValidoAte(catalogo.validoAte ? String(catalogo.validoAte).slice(0, 10) : "");
    setArquivo(null);
    setModalAberto(true);
  };

  const salvarEdicao = async () => {
    if (!nome.trim()) return toast.warn("Dê um nome ao catálogo");
    setEnviando(true);
    try {
      await api.put(`/catalogos/${editando.id}`, {
        nome: nome.trim(),
        descricao: descricao.trim(),
        validoAte: validoAte || null,
      });
      toast.success("Catálogo atualizado.");
      setModalAberto(false);
      limparFormulario();
      carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setEnviando(false);
    }
  };

  const alternarAtivo = async (catalogo) => {
    try {
      await api.put(`/catalogos/${catalogo.id}`, { ativo: !catalogo.ativo });
      carregar();
    } catch (err) {
      toastError(err);
    }
  };

  const retirar = async (catalogo) => {
    try {
      await api.delete(`/catalogos/${catalogo.id}`);
      toast.success("Catálogo retirado.");
      carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmar(null);
    }
  };

  const ehAdmin = ["ADMIN", "MASTERADMIN"].includes(
    String(user?.profile || "").toUpperCase()
  );

  return (
    <MainContainer sx={{ flex: 1, overflowY: "auto" }}>
      <ConfirmationModal
        title={confirmar ? `Retirar "${confirmar.nome}"?` : ""}
        open={!!confirmar}
        onClose={() => setConfirmar(null)}
        onConfirm={() => retirar(confirmar)}
      >
        O catálogo sai da lista das atendentes. As conversas em que ele já foi enviado
        não são afetadas.
      </ConfirmationModal>

      <MainHeader>
        <Title>Catálogos</Title>
        <MainHeaderButtonsWrapper>
          {ehAdmin && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutline />}
              onClick={() => setModalAberto(true)}
            >
              Cadastrar catálogo
            </Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper sx={{ flex: 1, p: 2, overflowY: "auto" }} variant="outlined">
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          O catálogo cadastrado aqui aparece para as atendentes dentro da conversa, no botão
          de anexo. O arquivo é preparado uma única vez — elas não precisam mais reduzir nada
          antes de enviar.
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Catálogo</TableCell>
              <TableCell align="center">Tamanho</TableCell>
              <TableCell align="center">Validade</TableCell>
              <TableCell align="center">Envios</TableCell>
              <TableCell align="center">Disponível</TableCell>
              <TableCell align="center">Editar</TableCell>
              <TableCell align="center">Retirar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {carregando && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            )}

            {!carregando && catalogos.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhum catálogo cadastrado ainda.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {catalogos.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {c.nome}
                  </Typography>
                  {c.descricao && (
                    <Typography variant="caption" color="text.secondary">
                      {c.descricao}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {emMB(c.tamanho)}
                  {c.tamanhoOrig > c.tamanho && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      era {emMB(c.tamanhoOrig)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {vencido(c.validoAte) ? (
                    <Chip size="small" color="warning" label={`venceu ${dataBR(c.validoAte)}`} />
                  ) : (
                    dataBR(c.validoAte)
                  )}
                </TableCell>
                <TableCell align="center">{c.envios || 0}</TableCell>
                <TableCell align="center">
                  <Tooltip title={c.ativo ? "Aparece para as atendentes" : "Não aparece"}>
                    <span>
                      <Switch
                        size="small"
                        checked={!!c.ativo}
                        disabled={!ehAdmin}
                        onChange={() => alternarAtivo(c)}
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar nome, descrição e validade">
                    <span>
                      <IconButton size="small" disabled={!ehAdmin} onClick={() => abrirEdicao(c)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    disabled={!ehAdmin}
                    onClick={() => setConfirmar(c)}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={modalAberto} onClose={() => !enviando && setModalAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editando ? "Editar catálogo" : "Cadastrar catálogo"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Nome"
              placeholder="Catálogo Boticário — Ciclo 13"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="Descrição (opcional)"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              fullWidth
            />
            <TextField
              label="Disponível até"
              type="date"
              value={validoAte}
              onChange={(e) => setValidoAte(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Depois desta data ele some da lista das atendentes. Deixe em branco para não vencer."
              fullWidth
            />
            {editando ? (
              <Typography variant="caption" color="text.secondary">
                Arquivo: {editando.nomeOriginal} ({emMB(editando.tamanho)}). Para trocar o
                arquivo, cadastre um catálogo novo e retire este.
              </Typography>
            ) : (
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFile />}
                sx={{ justifyContent: "flex-start" }}
              >
                {arquivo ? arquivo.name : "Escolher arquivo"}
                <input
                  ref={inputArquivo}
                  type="file"
                  hidden
                  onChange={(e) => setArquivo(e.target.files?.[0] || null)}
                />
              </Button>
            )}
            {!editando && arquivo && (
              <Typography variant="caption" color="text.secondary">
                {emMB(arquivo.size)}
                {arquivo.size > 8 * 1048576 &&
                  " — arquivos grandes são reduzidos automaticamente; isso pode levar alguns minutos."}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setModalAberto(false); limparFormulario(); }} disabled={enviando}>
            Cancelar
          </Button>
          <Button
            onClick={editando ? salvarEdicao : cadastrar}
            variant="contained"
            disabled={enviando}
          >
            {enviando
              ? (editando ? "Salvando…" : "Preparando o arquivo…")
              : (editando ? "Salvar" : "Cadastrar")}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Catalogos;
